import React, {
    CSSProperties,
    Dispatch,
    Fragment,
    KeyboardEvent as ReactKeyboardEvent,
    MutableRefObject,
    PropsWithChildren,
    Reducer,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState
} from "react";

// @ts-expect-error
import { css, CssObject, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { MotionContainerProps, indexOf } from "./components/motion-container";
import { useForcedNav } from "./hooks/use-forced-nav";
import { GLOBAL_NAV_HEIGHT } from "./nav-global";
import GlobalNavLink from "./nav-link";

export type DropdownStateProps = {
    readonly currentOpenDropDown: string;
    readonly setCurrentOpenDropDown: React.Dispatch<
        React.SetStateAction<string>
    >;
};

const fadeOutOverlayKeyFrames = keyframes({
    from: { opacity: 0.7, display: "block" },
    to: {
        opacity: 0,
        display: "none"
    }
});
const fadeOutKeyframes = keyframes({
    from: { opacity: 1, display: "block" },
    to: {
        opacity: 0,
        display: "none"
    }
});
const fadeOutOverlayAnimation = css({
    animation: `0.25s ${fadeOutOverlayKeyFrames} linear forwards`
});
const fadeOutAnimation = css({
    animation: `0.25s ${fadeOutKeyframes} linear forwards`
});

const fadeInOverlayKeyframes = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 0.7
    }
});
const fadeInKeyframes = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});
const fadeInOverlayAnimation = css({
    animation: `0.25s ${fadeInOverlayKeyframes} linear forwards`
});
const fadeInAnimation = css({
    animation: `0.25s ${fadeInKeyframes} linear forwards`
});

const displayBlock = css({
    display: "block"
});

const DropdownContentStyle = css({
    display: "none",
    opacity: 1,
    insetBlockStart: GLOBAL_NAV_HEIGHT,
    minWidth: "264px",
    width: "max-content",
    position: "absolute",
    overflow: "hidden",
    borderRadius: "15px",
    border: "none",
    boxShadow: `0 0 1px 0 ${wacToken(
        "color.neutral.light300"
    )}, 0 5px 20px -5px rgba(0,0,0,0.1)`,
    order: 100,
    backgroundColor: wacToken("color.neutral.light0"),
    transition: "width 200ms ease",
    zIndex: 10
});

const DropdownContentStyleGlobalNav = css({
    insetInlineStart: "0",
    insetInlineEnd: "0",
    marginInline: "auto"
});

const listContainer = css({
    display: "flex",
    alignItems: "center",
    height: "100%"
});

export const SubnavLayout = {
    Product: 4,
    Teams: 3,
    WhyAtlassian: 4,
    Resources: 4,
    Solutions: 4
} as const;

export type GlobalNavLinkContentProps = {
    readonly globalLinksKey: string;
    readonly linkText: string;
    readonly hasSubnav: boolean;
    readonly isActive?: boolean;
    readonly href?: string;
    readonly productKey?: string;
    readonly content?: JSX.Element | string;
    readonly layout?: SubnavLayout;
    readonly containerPosition?: CSSProperties["position"];
    readonly containerStyles?: CssObject<unknown> | CssObject<unknown>[];
};

function layoutToWidth(layout: SubnavLayout | undefined) {
    switch (layout) {
        case 4:
            return "57rem";
        case 3:
            return "43.5rem";
        default:
            return "auto";
    }
}

type GlobalNavState = {
    current: string | null;
    prev: string | null;
};

type GlobalNavContextType = GlobalNavState & {
    globalNavItemKeys: Array<string>;
    direction: MotionContainerProps["direction"];
    setCurrent: Dispatch<string | null>;
    dropdownRefCallback: (node: HTMLDivElement | null) => void;
    dropdownRefCurrentNode: HTMLDivElement | null;
    isLoggedInUserProfileDropdownOpen: boolean;
    setIsLoggedInUserProfileDropdownOpen: Dispatch<SetStateAction<boolean>>;
};

const GlobalNavContext = createContext<GlobalNavContextType | null>(null);

export const GlobalNavProvider = ({
    globalNavItemKeys,
    children
}: PropsWithChildren<{ globalNavItemKeys: Array<string> }>) => {
    const { forcedNavKey, isForcingNav } = useForcedNav();

    const [dropdownRefCurrentNode, setDropdownRefCurrent] =
        useState<HTMLDivElement | null>(null);

    const [
        isLoggedInUserProfileDropdownOpen,
        setIsLoggedInUserProfileDropdownOpen
    ] = useState(false);

    // using callback ref to set the isGlobalNavDropdownOpen state when dropdownRef is updated
    const dropdownRefCallback = useCallback((node: HTMLDivElement | null) => {
        setDropdownRefCurrent(node);
    }, []);

    const [state, dispatch] = useReducer<
        Reducer<GlobalNavState, string | null>
    >(
        function reducer(state, action) {
            // If we're forcing a nav item, ignore state changes (local dev only)
            if (isForcingNav && forcedNavKey) {
                return { ...state, current: forcedNavKey, prev: state.current };
            }

            const prev = state.current;
            return { ...state, current: action, prev };
        },
        {
            // Initialize with forced nav if present (local development only)
            current: isForcingNav && forcedNavKey ? forcedNavKey : null,
            prev: null
        }
    );

    const { current, prev } = state;

    const value = useMemo(() => {
        const currentIndex = indexOf(globalNavItemKeys, (navItemKey) => {
            return navItemKey === current;
        });

        const prevIndex = indexOf(globalNavItemKeys, (navItemKey) => {
            return navItemKey === prev;
        });

        const direction: MotionContainerProps["direction"] =
            prev === "exitDiv"
                ? "none"
                : currentIndex === -1
                ? "down"
                : prevIndex === -1
                ? "up"
                : "none";

        return {
            current,
            prev,
            globalNavItemKeys,
            direction,
            setCurrent: dispatch,
            dropdownRefCallback,
            dropdownRefCurrentNode,
            isLoggedInUserProfileDropdownOpen,
            setIsLoggedInUserProfileDropdownOpen
        };
    }, [
        current,
        prev,
        globalNavItemKeys,
        dropdownRefCallback,
        dropdownRefCurrentNode,
        isLoggedInUserProfileDropdownOpen,
        setIsLoggedInUserProfileDropdownOpen
    ]);

    return (
        <GlobalNavContext.Provider value={value}>
            {children}
        </GlobalNavContext.Provider>
    );
};

export const useGlobalNavContext = () => {
    const context = useContext(GlobalNavContext);

    if (!context) {
        throw new Error(
            "useGlobalNavContext must be used within GlobalNavProvider"
        );
    }

    return context;
};

export const useExitCurrentNavOnHover = () => {
    const { setCurrent } = useGlobalNavContext();

    const handleMouseLeave = useCallback(() => {
        setCurrent("exitDiv");
    }, [setCurrent]);

    return { handleMouseLeave };
};

export const DivOnHoverExit = ({
    children
}: {
    children: React.ReactElement;
}) => {
    const { handleMouseLeave } = useExitCurrentNavOnHover();
    const navRef = useRef<HTMLDivElement>(null);

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLDivElement>) => {
            // Only close the dropdown if focus is moving outside the nav area
            if (
                navRef.current &&
                e.relatedTarget &&
                !navRef.current.contains(e.relatedTarget as Node)
            ) {
                handleMouseLeave();
            }
        },
        [handleMouseLeave]
    );

    return (
        <div
            ref={navRef}
            css={css({ height: "100%" })}
            onMouseLeave={handleMouseLeave}
            onBlur={handleBlur}
        >
            {children}
        </div>
    );
};

const MoreOrLess = ({
    showButton,
    isShowingMore,
    onClick
}: {
    showButton: boolean;
    onClick: () => void;
    isShowingMore?: boolean;
}) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const visibility: CSSProperties["visibility"] = showButton
        ? "visible"
        : "hidden";

    const handleClick = () => {
        onClick();
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "linkButton",
            source: isShowingMore ? "globalNav-less" : "globalNav-more",
            attributes: {
                label: isShowingMore ? "less--" : "more-+"
            }
        }).fire();
    };

    const onKeyDown = (e: ReactKeyboardEvent) => {
        if (e.code === "Enter" || e.code === "Space") {
            handleClick();
        }
    };

    return (
        <li
            aria-hidden={true}
            style={{
                visibility,
                position: showButton ? "initial" : "absolute"
            }}
            data-element-globalnav={isShowingMore ? "less" : "more"}
            data-element-visibility={visibility}
            data-testid="moreOrLess-button"
            css={listContainer}
        >
            <span
                role="button"
                tabIndex={0}
                onClick={handleClick}
                onKeyDown={onKeyDown}
                css={css({
                    paddingBlockStart: token("space.100"),
                    display: "flex",
                    alignItems: "center",
                    lineHeight: "1rem",
                    cursor: "pointer",
                    ["&:hover"]: {
                        color: wacToken("color.extended.blue800")
                    }
                })}
            >
                {isShowingMore ? (
                    <span
                        css={css({ color: wacToken("color.neutral.light700") })}
                    >
                        {intl.formatMessage({
                            id: "globalNav.V2.navlink.lessMinus",
                            defaultMessage: "Less -"
                        })}
                    </span>
                ) : (
                    <span>
                        {intl.formatMessage({
                            id: "globalNav.V2.navlink.morePlus",
                            defaultMessage: "More +"
                        })}
                    </span>
                )}
            </span>
        </li>
    );
};

const GlobalNavLinksContainer = ({
    globalLinksProps,
    globalLinksRef,
    maxLinks = globalLinksProps.length,
    showMore,
    setShowMore,
    isSubnav = false
}: {
    globalLinksProps: GlobalNavLinkContentProps[];
    globalLinksRef?: MutableRefObject<HTMLUListElement | null>;
    maxLinks?: number;
    showMore?: boolean;
    setShowMore?: Dispatch<SetStateAction<boolean>>;
    isSubnav?: boolean;
}) => {
    const {
        current,
        setCurrent,
        dropdownRefCallback,
        prev,
        dropdownRefCurrentNode
    } = useGlobalNavContext();
    const closedDropdownRef = useRef<HTMLDivElement | null>(null);

    const handleEscKeyEvent = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setCurrent("");
            }
        },
        [setCurrent]
    );

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (
                dropdownRefCurrentNode &&
                !dropdownRefCurrentNode.contains(event.target as Node)
            ) {
                setCurrent("");
            }
        },
        [setCurrent, dropdownRefCurrentNode]
    );

    useEffect(() => {
        // Attach the listeners on component mount.
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscKeyEvent, false);
        // Detach the listeners on component unmount.
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscKeyEvent, false);
        };
    }, [handleEscKeyEvent, handleClickOutside]);

    useEffect(() => {
        if (current === "exitDiv") {
            setCurrent("");
        }
    }, [current, setCurrent]);

    const showMoreButton = maxLinks < globalLinksProps.length;

    const showMoreGridTemplateColumns = showMore
        ? globalLinksProps.length + 1
        : maxLinks === globalLinksProps.length
        ? maxLinks
        : maxLinks + 1;

    return (
        <>
            {globalLinksProps.find(
                (globalLink) =>
                    globalLink.globalLinksKey === current &&
                    globalLink.hasSubnav
            ) && (
                <div
                    css={[
                        css({
                            position: "fixed",
                            display: "none",
                            width: "100%",
                            height: "100%",
                            insetBlockStart: "0",
                            insetInlineStart: "0",
                            insetInlineEnd: "0",
                            insetBlockEnd: "0",
                            opacity: 0.7,
                            backgroundColor: wacToken("color.neutral.light0"),
                            zIndex: "-1"
                        }),
                        displayBlock,
                        !prev && fadeInOverlayAnimation,
                        !current && fadeOutOverlayAnimation
                    ]}
                />
            )}
            <DivOnHoverExit>
                <ul
                    className="glob-nav-links-container-ul"
                    css={[
                        css({
                            display: "grid",
                            gridTemplateColumns: "repeat(5, max-content)",
                            alignItems: "center",
                            listStyle: "none",
                            margin: 0,
                            padding: 0,
                            height: "100%",
                            [media.above.md]: {
                                marginInlineStart: token("space.400")
                            }
                        })
                    ]}
                    style={{
                        gridTemplateColumns: `repeat(${
                            !isSubnav ? showMoreGridTemplateColumns : maxLinks
                        }, max-content)`
                    }}
                    ref={globalLinksRef}
                >
                    {globalLinksProps.map(
                        // eslint-disable-next-line complexity
                        (globalLink: GlobalNavLinkContentProps, index) => {
                            const containerPosition =
                                globalLink.containerPosition || "initial";
                            const isVisible =
                                globalLink.hasSubnav &&
                                globalLink.globalLinksKey === current;
                            const shouldFadeIn = isVisible && !prev;
                            const shouldShowMore =
                                index + 1 <= maxLinks || showMore;

                            const computedLayoutNumber = globalLink?.layout;

                            return (
                                <Fragment key={globalLink.globalLinksKey}>
                                    <li
                                        style={{
                                            position: shouldShowMore
                                                ? containerPosition
                                                : "absolute",
                                            visibility: shouldShowMore
                                                ? "visible"
                                                : "hidden"
                                        }}
                                        css={[
                                            listContainer,
                                            isSubnav &&
                                                css({
                                                    marginBlockEnd:
                                                        token("space.075")
                                                })
                                        ]}
                                    >
                                        <GlobalNavLink {...globalLink} />
                                        <div
                                            css={[
                                                DropdownContentStyle,
                                                !isSubnav &&
                                                    DropdownContentStyleGlobalNav,
                                                shouldFadeIn && fadeInAnimation,
                                                isVisible && displayBlock,
                                                !isVisible && fadeOutAnimation
                                            ]}
                                            ref={
                                                isVisible
                                                    ? dropdownRefCallback
                                                    : closedDropdownRef
                                            }
                                            aria-hidden={!globalLink?.content}
                                            style={{
                                                width: layoutToWidth(
                                                    computedLayoutNumber
                                                ),
                                                ...globalLink?.containerStyles
                                            }}
                                        >
                                            {globalLink.content}
                                        </div>
                                    </li>
                                </Fragment>
                            );
                        }
                    )}
                    {!isSubnav && !!setShowMore && (
                        <MoreOrLess
                            showButton={showMoreButton}
                            isShowingMore={showMore}
                            onClick={() => setShowMore((prev) => !prev)}
                        />
                    )}
                </ul>
            </DivOnHoverExit>
        </>
    );
};

export const DropdownContentWrapper = ({ children }: PropsWithChildren) => (
    <div>{children}</div>
);

export default GlobalNavLinksContainer;

export type SubnavLayout = (typeof SubnavLayout)[keyof typeof SubnavLayout];
